import React from "react";
import type { ApolloClient } from "@apollo/client";
import { each, find } from "lodash";
import { push } from "store/actions/router";
import { Col, Row } from "react-bootstrap";
import { connect, type ConnectedProps } from "react-redux";
import {
  getInvestmentProfileAndSuitability,
  startInvestmentProfileUpdate,
} from "actions/userActions";
import { getAllAccounts } from "actions/accountActions";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { userConstants } from "actions/types";
import { scrollToTop } from "utils/dom";
import {
  experienceValues,
  timeHorizonLiquidityValues,
  riskToleranceValues,
  incomeLabel,
  wealthLabel,
  hideLiquidInvestment,
} from "utils/investProfileHelper";
import { getIraAccountIdSelector } from "store/selectors/user";
import type { RootState } from "types/state";
import type { GetInvestmentProfileAndSuitabilityQuery } from "types/graphql";

import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import Button from "components/Button";
import Alert from "components/Alert";
import IconSpinner from "components/IconSpinner";

import "./SuitabilityProfile.scss";

const actions = [userConstants.USER_INVESTMENT_PROFILE_UPDATE];

const updatingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => ({
  error: errorSelector(state),
  isUpdating: updatingSelector(state),
  // selectors/user is typed loosely at the boundary — cast to the value used here
  accountId: getIraAccountIdSelector(state) as string,
});

const mapDispatchToProps = {
  push,
  startInvestmentProfileUpdate,
  getInvestmentProfileAndSuitability,
  getAllAccounts,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

// `incomeLabel` (utils/investProfileHelper) expects a range object; this mirrors
// the shape returned by the getInvestmentProfile query for the income field.
interface IncomeValue {
  rawValue?: number | string;
  min?: number;
}

// State mirrors the JS shape: initialized with string literals, then replaced
// by the spread of the getInvestmentProfile/getSuitability query results. The
// index signatures accommodate the spread of the (wider) query objects.
interface InvestmentProfileState {
  objective?: string;
  experience?: string;
  // initial state is a string; after the fetch this holds the income range
  // object that `incomeLabel` consumes
  annualIncome?: string | IncomeValue;
  liquidNetWorth?: React.ReactNode;
  totalNetWorth?: string;
  riskTolerance?: string;
  [key: string]: unknown;
}

interface SuitabilityState {
  timeHorizon?: string;
  liquidityNeeds?: string;
  timeHorizonLiquidity?: string;
  [key: string]: unknown;
}

interface State {
  investmentProfile: InvestmentProfileState;
  suitability: SuitabilityState;
  submitting: boolean;
  loading: boolean;
}

class SuitabilityProfile extends React.PureComponent<Props, State> {
  _isMounted = false;

  constructor(props: Props) {
    super(props);
    this.state = {
      investmentProfile: {
        objective: "",
        experience: "",
        annualIncome: "",
        liquidNetWorth: "None",
        totalNetWorth: "",
        riskTolerance: "",
      },
      suitability: {
        timeHorizon: "",
        liquidityNeeds: "",
        timeHorizonLiquidity: "",
      },
      submitting: false,
      loading: true,
    };
  }

  // PRESERVED BUG (conversion-only): the action returns a plain Promise with no
  // .cancel(); componentWillUnmount still calls .cancel() on each entry. Typed
  // as a cancelable to mirror the original (latent) assumption.
  _promises: { cancel: () => void }[] = [];

  componentDidMount() {
    const { accountId } = this.props;
    const usPromise = this.props
      .getInvestmentProfileAndSuitability(
        this.props.client as ApolloClient<object>,
        accountId
      )
      .then((data) => {
        // action resolves to the query data (or {} on error) — cast at the
        // boundary, mirroring the converted IndividualInvestmentProfile page
        const profileData = data as GetInvestmentProfileAndSuitabilityQuery;
        const investmentProfileInfo = data && profileData.getInvestmentProfile;
        const suitability = data && profileData.getSuitability;
        let investmentProfile = {
          ...investmentProfileInfo,
          liquidNetWorth: wealthLabel(
            investmentProfileInfo!.liquidNetWorth as Parameters<
              typeof wealthLabel
            >[0]
          ),
          totalNetWorth: wealthLabel(
            investmentProfileInfo!.totalNetWorth as Parameters<
              typeof wealthLabel
            >[0]
          ),
          riskTolerance: investmentProfileInfo!.riskToleranceRawValue,
        } as InvestmentProfileState;
        this.setState({
          investmentProfile,
          suitability: suitability as SuitabilityState,
          loading: false,
        });
      });
    // `getInvestmentProfileAndSuitability` returns a thenable without a cancel
    // method; the original pushes it here and calls .cancel() on unmount
    this._promises.push(usPromise as unknown as { cancel: () => void });
    scrollToTop();
  }

  componentWillUnmount() {
    each(this._promises, (promise) => promise.cancel());
  }

  updateInvestmentProfile = async () => {
    this.setState({ submitting: true });
    await this.props
      .startInvestmentProfileUpdate(this.props.client as ApolloClient<object>)
      .then(() =>
        this.setState(
          { submitting: false },
          // PRESERVED BUG (conversion-only): the connected `push` thunk returns
          // void, so this dispatches the navigation eagerly and passes the void
          // result as setState's callback rather than a callback function.
          // @ts-expect-error void is not a valid setState callback
          this.props.push("/individual/investment")
        )
      )
      .catch(() => this.setState({ submitting: false }));
  };

  render() {
    if (this.state.loading) {
      return <IconSpinner centered />;
    }

    return this.renderForm();
  }

  renderForm = () => {
    const { investmentProfile, suitability } = this.state;

    return (
      <div className="investment-profile-container">
        <Row>
          <Col>
            <div>
              <div className="header-section">
                <IconHeader
                  variant="cardHeader"
                  headerText="Investment Profile"
                />
                <IconSubheader
                  subheader="Let us know if anything changes by updating your profile and
                  we can make sure your portfolio is in tip-top shape together!"
                />
              </div>
            </div>

            <div className="information-section">
              <p className="title">What is your pre-tax income this year?</p>
              <p className="subtext">
                {incomeLabel(investmentProfile.annualIncome as IncomeValue)}
              </p>
            </div>

            <div className="information-section">
              <p className="title">
                Please estimate the value of your cash, investments, and
                retirement accounts.
              </p>
              <p className="subtext">{investmentProfile.totalNetWorth}</p>
            </div>
            {!hideLiquidInvestment(investmentProfile.totalNetWorth) && (
              <div className="information-section">
                <p className="title">
                  How much of your cash holdings are being held in a checking or
                  savings account?
                </p>
                <p className="subtext">{investmentProfile.liquidNetWorth}</p>
              </div>
            )}
            <div className="information-section">
              <p className="title">
                When you think about how your money is invested, which one of
                the following statements describes your investment goal?
              </p>
              <p className="subtext">
                {
                  // PRESERVED BUG (conversion-only): `find` can return undefined
                  // and `.description` is read directly — throws if no match.
                  // The `!` preserves the original throw-on-undefined behavior.
                  find(
                    riskToleranceValues,
                    (item) => item.value === investmentProfile.riskTolerance
                  )!.description
                }
              </p>
            </div>
            <div className="information-section">
              <p className="title">
                What is your current level of investment experience?
              </p>
              <p className="subtext">
                {
                  // PRESERVED BUG (conversion-only): `find` may return undefined;
                  // `!` preserves the original throw-on-undefined behavior.
                  find(
                    experienceValues,
                    (item) => item.value === investmentProfile.experience
                  )!.description
                }
              </p>
            </div>
            <div className="information-section">
              <p className="title">
                When do you anticipate needing the money in your investment
                account?
              </p>
              <p className="subtext">
                {
                  // PRESERVED BUG (conversion-only): `find` may return undefined;
                  // `!` preserves the original throw-on-undefined behavior.
                  find(
                    timeHorizonLiquidityValues,
                    (item) => item.value === suitability.timeHorizonLiquidity
                  )!.description
                }
              </p>
            </div>
            <div className="submit-row">
              {this.props.error && (
                <Alert type="error" msg={this.props.error} />
              )}
              <Button
                size="sm"
                withArrow={true}
                name="submit"
                btnLabel="Update Profile"
                loading={this.props.isUpdating || this.state.submitting}
                onClick={this.updateInvestmentProfile}
              />
            </div>
          </Col>
        </Row>
      </div>
    );
  };
}

export default connector(withApollo<Props>(SuitabilityProfile));
